import { CountryCode } from '../types/locale'

export enum APIGeeEndpoint {
  FordPayCheckoutSigning = '/pro/e-comm/fordpay-checkout-signing',
  PostPayment = '/pro/e-comm/post',
}

export enum HereMapApi {
  AutoSuggest = 'https://autocomplete.search.hereapi.com/v1/',
}

export const countryCodeUSA = 'countryCode:USA'
export const countryCodeCAN = 'countryCode:CAN'

export const hereMapsCountryCodes: { [key in CountryCode]: string } = {
  US: countryCodeUSA,
  CA: countryCodeCAN,
}

export const HouseNumber = 'houseNumber'

export enum HereMapEndpoint {
  Geocode = '/geocode',
  AutoComplete = '/autocomplete',
}

export enum EcomUniversalContextScope {
  Ecom = 'ecom',
}

export enum EcomUniversalContextProperty {
  Customer = 'ecomCustomer',
  ShippingAddress = 'ecomShippingAddress',
  ShippingAddressGeocode = 'ecomShippingAddressGeocode',
}

export const NAME_REGEX = /^[A-Za-z]+\D+$/
export const FIN_REGEX = /^\w{5}$/

// Rules on company names are unclear and inconsistent. The state of Delaware allows a lot of special characters,
// while the IRS doesn't even allow accented characters. It shouldn't hurt to allow users to store a lot, though
// Source: https://www.delawareinc.com/blog/using-symbol-punctuation-in-business-name/#:~:text=Per%20the%20IRS%2C%20the%20only,ampersand%20(%26)
// Delaware allows: ! “ ” # $ % & ’ () * + ,- . ? / \ : ;@ { } [ ] ~_^ ` |
export const COMPANY_NAME_REGEX =
  /^[!"“”#’$%&'()*+,\-.?\/\\:;@{}[\]~^`| \wÀ-Ÿ]+$/
export const EMAIL_REGEX = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i
export const ZIP_REGEX = /^\d{5}(?:[-\s]?\d{4})?$/
export const CA_POSTAL_REGEX =
  /^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z][ -]?\d[ABCEGHJ-NPRSTV-Z]\d$/i
export const PHONE_REGEX = /^\(?(\d{3})\)?[-.●]?(\d{3})[-.●]?(\d{4})$/
export const ADDRESS_REGEX = /^(\d{0,10}\S?\w?)\s?[a-zA-Z0-9\s]+(\.)?/
export const CITY_REGEX = /[a-zA-Z]+/

export interface DropDownValue<T extends string | number> {
  label: string
  value: T
}

export function generateNumberedDropdownOptions(
  count: number,
): DropDownValue<number>[] {
  return Array.from({ length: count }, (_, index) => ({
    label: (index + 1).toString(),
    value: index + 1,
  }))
}

export const dropdownOptions: DropDownValue<number>[] =
  generateNumberedDropdownOptions(10)

export const CaProvinceOptions: DropDownValue<string>[] = [
  { label: 'AB', value: 'Alberta' },
  { label: 'BC', value: 'BritishColumbia' },
  { label: 'MB', value: 'Manitoba' },
  { label: 'NB', value: 'NewBrunswick' },
  { label: 'NL', value: 'NewfoundlandandLabrador' },
  { label: 'NT', value: 'NorthwestTerritories' },
  { label: 'NS', value: 'NovaScotia' },
  { label: 'NU', value: 'Nunavut' },
  { label: 'ON', value: 'Ontario' },
  { label: 'PE', value: 'PrinceEdwardIsland' },
  { label: 'QC', value: 'Québec' },
  { label: 'SK', value: 'Saskatchewan' },
  { label: 'YT', value: 'Yukon' },
]

export const UsStateOptions: DropDownValue<string>[] = [
  { label: 'AK', value: 'Alaska' },
  { label: 'AL', value: 'Alabama' },
  { label: 'AR', value: 'Arkansas' },
  { label: 'AZ', value: 'Arizona' },
  { label: 'CA', value: 'California' },
  { label: 'CO', value: 'Colorado' },
  { label: 'CT', value: 'Connecticut' },
  { label: 'DC', value: 'DistrictofColumbia' },
  { label: 'DE', value: 'Delaware' },
  { label: 'FL', value: 'Florida' },
  { label: 'GA', value: 'Georgia' },
  { label: 'HI', value: 'Hawaii' },
  { label: 'IA', value: 'Iowa' },
  { label: 'ID', value: 'Idaho' },
  { label: 'IL', value: 'Illinois' },
  { label: 'IN', value: 'Indiana' },
  { label: 'KS', value: 'Kansas' },
  { label: 'KY', value: 'Kentucky' },
  { label: 'LA', value: 'Louisiana' },
  { label: 'MA', value: 'Massachusetts' },
  { label: 'MD', value: 'Maryland' },
  { label: 'ME', value: 'Maine' },
  { label: 'MI', value: 'Michigan' },
  { label: 'MN', value: 'Minnesota' },
  { label: 'MO', value: 'Missouri' },
  { label: 'MS', value: 'Mississippi' },
  { label: 'MT', value: 'Montana' },
  { label: 'NC', value: 'NorthCarolina' },
  { label: 'ND', value: 'NorthDakota' },
  { label: 'NE', value: 'Nebraska' },
  { label: 'NH', value: 'NewHampshire' },
  { label: 'NJ', value: 'NewJersey' },
  { label: 'NM', value: 'NewMexico' },
  { label: 'NV', value: 'Nevada' },
  { label: 'NY', value: 'NewYork' },
  { label: 'OH', value: 'Ohio' },
  { label: 'OK', value: 'Oklahoma' },
  { label: 'OR', value: 'Oregon' },
  { label: 'PA', value: 'Pennsylvania' },
  { label: 'RI', value: 'RhodeIsland' },
  { label: 'SC', value: 'SouthCarolina' },
  { label: 'SD', value: 'SouthDakota' },
  { label: 'TN', value: 'Tennessee' },
  { label: 'TX', value: 'Texas' },
  { label: 'UT', value: 'Utah' },
  { label: 'VA', value: 'Virginia' },
  { label: 'VT', value: 'Vermont' },
  { label: 'WA', value: 'Washington' },
  { label: 'WI', value: 'Wisconsin' },
  { label: 'WV', value: 'WestVirginia' },
  { label: 'WY', value: 'Wyoming' },
]

export const stateProvinceMap: Record<CountryCode, DropDownValue<string>[]> = {
  US: UsStateOptions,
  CA: CaProvinceOptions,
}

export const countryRegionOptions: DropDownValue<CountryCode>[] = [
  { label: 'US', value: 'US' },
  { label: 'CA', value: 'CA' },
]
